import React from 'react';

import CleanBanner from '@/components/corporate/CleanBanner';
import Seo from '@/components/seo/Seo';
import { ROUTE_SEO, buildProductLd } from '@/config/seo';
import DesignerImage from '@/assets/product/data-finsuer-designer.png';

const RED = '#b8313d';

// 구성요소 헤더의 역할별 흐릿한 배경 모티프(인라인 SVG, opacity는 SCSS).
const motifs: Record<string, React.ReactNode> = {
  Gateway: (
    <svg viewBox="0 0 120 120" className="cp-comp__motif" aria-hidden>
      <line x1={30} y1={60} x2={92} y2={28} stroke={RED} strokeWidth={3} />
      <line x1={30} y1={60} x2={92} y2={92} stroke={RED} strokeWidth={3} />
      <circle cx={30} cy={60} r={12} fill={RED} />
      <circle cx={94} cy={28} r={9} fill={RED} />
      <circle cx={94} cy={92} r={9} fill={RED} />
    </svg>
  ),
  Designer: (
    <svg viewBox="0 0 120 120" className="cp-comp__motif" aria-hidden>
      <rect x={24} y={24} width={72} height={72} rx={6} fill="none" stroke={RED} strokeWidth={3} />
      <line x1={48} y1={24} x2={48} y2={96} stroke={RED} strokeWidth={2} />
      <line x1={72} y1={24} x2={72} y2={96} stroke={RED} strokeWidth={2} />
      <line x1={24} y1={52} x2={96} y2={52} stroke={RED} strokeWidth={2} />
    </svg>
  ),
  Loader: (
    <svg viewBox="0 0 120 120" className="cp-comp__motif" aria-hidden>
      <rect x={24} y={30} width={72} height={18} rx={9} fill={RED} />
      <rect x={24} y={54} width={72} height={18} rx={9} fill={RED} opacity={0.7} />
      <rect x={24} y={78} width={72} height={18} rx={9} fill={RED} opacity={0.45} />
    </svg>
  ),
  Executor: (
    <svg viewBox="0 0 120 120" className="cp-comp__motif" aria-hidden>
      <polygon points="44,28 94,60 44,92" fill={RED} />
    </svg>
  ),
  Manager: (
    <svg viewBox="0 0 120 120" className="cp-comp__motif" aria-hidden>
      <rect x={30} y={64} width={14} height={32} fill={RED} />
      <rect x={52} y={42} width={14} height={54} fill={RED} />
      <rect x={74} y={54} width={14} height={42} fill={RED} />
    </svg>
  ),
};

const sources = ['CSV', 'XLSX', 'Oracle', 'MySQL', 'MariaDB', 'Tibero'];
const endpoints = ['GET /api/users', 'GET /api/orders', 'GET /api/search'];

const diffs = [
  {
    no: '01',
    title: '기술문서 자동 생성',
    desc: '제공 항목·스키마·파라미터를 정의하면 기술문서가 동시에 자동 생성됩니다.',
  },
  {
    no: '02',
    title: '자동 현행화',
    desc: 'Open API 세부사항이 변경되면 기술문서도 자동 갱신되어 현행성을 유지합니다.',
  },
  { no: '03', title: '개발자 친화 환경', desc: '별도 문서 편집기 없이 기술문서를 바로 참조할 수 있습니다.' },
  { no: '04', title: '비개발자도 쉽게', desc: '사용자 친화적인 UI/UX로 누구나 운영·관리할 수 있습니다.' },
  { no: '05', title: '국제표준 준용', desc: '국제표준 OAS v3.0을 준용해 호환성을 확보합니다.' },
];

const characteristics = [
  {
    no: '01',
    title: 'API 즉시 생성',
    desc: '코딩 없이 GUI 조작만으로 즉시 API를 생성·관리. 파일을 업로드하면 자동으로 DB에 적재됩니다.',
  },
  {
    no: '02',
    title: '자동화된 API 문서',
    desc: 'Swagger 대화형 UI로 API 명세를 자동 제공. 시스템을 몰라도 문서만으로 연동할 수 있습니다.',
  },
  {
    no: '03',
    title: '다양한 데이터 소스',
    desc: 'CSV·XLSX부터 Oracle·MySQL·MariaDB·CUBRID·Tibero까지. 스케줄러가 변경분을 자동 수집·갱신합니다.',
  },
  {
    no: '04',
    title: '마이크로서비스 아키텍처',
    desc: '트래픽이 몰려도 안정적. 서비스 독립성으로 탄력적 배포·확장, HTTP 2.0 RPC로 빠른 성능.',
  },
];

const components = [
  {
    name: 'Gateway',
    ko: '게이트웨이',
    desc: '사용자 요청에 따라 API를 호출하고 결과를 반환합니다. 인증키 기반 권한 확인, 호출량 제어, 캐싱과 헬스체크로 안정적인 서비스를 보장합니다.',
    mods: ['API 연계', '인증', '트래픽 제어', 'Caching'],
  },
  {
    name: 'Designer',
    ko: 'GUI 기반 API 관리도구',
    desc: 'Raw Data로부터 API를 생성·관리합니다. 제공 항목·명세·파라미터를 정의하면 OAS 3.0 기반 명세를 구성하고 배포합니다.',
    mods: ['소스 관리', 'API 생성', 'API 정의', 'OAS 3.0 명세'],
  },
  {
    name: 'Loader',
    ko: '원천 데이터 수집·연계',
    desc: 'Raw Data에서 데이터를 추출·분석·저장합니다. 스키마를 정의해 메타데이터 DB에 적재하고 설정 주기에 따라 자동 갱신합니다.',
    mods: ['데이터 수집', '데이터 연계', '메타데이터 추출', '스키마 생성'],
  },
  {
    name: 'Executor',
    ko: 'Open API 서비스 구동',
    desc: '사용자 요청을 실행해 필요한 데이터를 조회합니다. 메타데이터 질의로 Open Data를 찾아 Gateway로 전달합니다.',
    mods: ['REST 서비스', '파라미터 검증', 'Request 분석', '데이터 조회'],
  },
  {
    name: 'Manager',
    ko: '서비스 관리 시스템',
    desc: '배포된 API의 상태·로그를 관리하고 다차원 분석·통계를 제공합니다. 트래픽 변화와 API별 호출 수를 시각화합니다.',
    mods: ['이용자 관리', '로그 조회', '다차원 분석', '시각화'],
  },
];

const DataInfuser: React.FC = () => {
  return (
    <div className="cp">
      <Seo
        title={ROUTE_SEO.infuser.title}
        description={ROUTE_SEO.infuser.description}
        path={ROUTE_SEO.infuser.path}
        jsonLd={buildProductLd('infuser', 'Data Infuser')}
      />
      <CleanBanner
        eyebrow="PRODUCT"
        title="Data Infuser"
        desc="오픈소스 기반 API 관리 시스템 · 데이터 인퓨저"
        meta={['오픈소스', 'OAS v3.0 준용', '마이크로서비스 아키텍처']}
      />

      {/* 소개 */}
      <section className="cp-section">
        <div className="cp__container">
          <div className="cp-intro">
            <div>
              <div className="cp-eyebrow">WHAT IS IT</div>
              <h2>데이터 인퓨저란?</h2>
              <p>
                다양한 원천 데이터를 손쉽게 API로 전환하는 <b className="cp-emph">오픈소스 기반 API 관리 시스템</b>
                입니다. 코딩 없이 데이터를 개방 가능한 서비스로 만들어, 데이터 보유 기관의 API 구축 부담을 크게
                줄입니다.
              </p>
            </div>
            <div className="cp-flow">
              <div className="cp-flow__row">
                <div className="cp-flow__label">원천 데이터 (SOURCE)</div>
                <div className="cp-flow__tags">
                  {sources.map((s) => (
                    <span key={s} className="cp-flow__tag">
                      {s}
                    </span>
                  ))}
                </div>
                <div className="cp-flow__caption">다양한 파일·DBMS를 그대로 연결</div>
              </div>
              <div className="cp-flow__arrow">↓</div>
              <div className="cp-flow__core">
                <div className="cp-flow__core-title">Data Infuser</div>
                <div className="cp-flow__core-sub">코딩 없이 데이터를 API로 자동 변환</div>
              </div>
              <div className="cp-flow__arrow">↓</div>
              <div className="cp-flow__row">
                <div className="cp-flow__label">Open API</div>
                <div className="cp-flow__tags">
                  {endpoints.map((ep) => (
                    <span key={ep} className="cp-flow__tag cp-flow__tag--mono">
                      {ep}
                    </span>
                  ))}
                </div>
                <div className="cp-flow__caption">표준 OAS 3.0 · REST · JSON / XML 제공</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 차별성 */}
      <section className="cp-section cp-section--soft">
        <div className="cp__container">
          <div className="cp-lead">
            데이터 인퓨저는 다양한 사업에서 <span className="cp-emph">API 구축에 활용</span>되고 있습니다.
          </div>
          <div className="cp-intro">
            <div className="cp-shot">
              <img src={DesignerImage} alt="Data Infuser Designer GUI" />
            </div>
            <div>
              <div className="cp-eyebrow">DIFFERENTIATION</div>
              <div className="cp-numlist">
                {diffs.map((d) => (
                  <div key={d.no} className="cp-numlist__row">
                    <div className="cp-numlist__no">{d.no}</div>
                    <div>
                      <div className="cp-numlist__title">{d.title}</div>
                      <div className="cp-numlist__desc">{d.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 특장점 */}
      <section className="cp-section">
        <div className="cp__container">
          <div className="cp-head">
            <div className="cp-head__eyebrow">KEY STRENGTHS</div>
            <h2 className="cp-head__title">Data Infuser의 특장점</h2>
          </div>
          <div className="cp-cards cp-cards--4">
            {characteristics.map((c) => (
              <div key={c.no} className="cp-card">
                <div className="cp-card__no">{c.no}</div>
                <div className="cp-card__title">{c.title}</div>
                <div className="cp-card__desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 구성요소 */}
      <section className="cp-section cp-section--soft-top">
        <div className="cp__container">
          <div className="cp-head">
            <div className="cp-head__eyebrow">ARCHITECTURE</div>
            <h2 className="cp-head__title">Data Infuser 구성요소</h2>
          </div>
          <div className="cp-comp">
            {components.map((c) => (
              <div key={c.name} className="cp-comp__card">
                <div className="cp-comp__head">
                  {motifs[c.name]}
                  <span className="cp-comp__badge">{c.name}</span>
                  <div className="cp-comp__ko">{c.ko}</div>
                </div>
                <div className="cp-comp__body">
                  <p className="cp-comp__desc">{c.desc}</p>
                  <div className="cp-comp__mods">
                    {c.mods.map((m) => (
                      <span key={m} className="cp-comp__mod">
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataInfuser;
